import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useTenants } from '@/lib/api/admin';
import { LogOut, RefreshCw, Bell, Menu } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { supportedLanguages } from '@/i18n';

export function Header({ mobileOpen = false, onOpenMenu }: { mobileOpen?:boolean; onOpenMenu?:()=>void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, selectedTenantId, setSelectedTenant } = useAuthStore();
  const { data: tenants } = useTenants(user?.role === 'admin');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isAdmin = user?.role === 'admin';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || null;
    setSelectedTenant(value);
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['one-metrics'] });
  };

  const tenantOptions = isAdmin
    ? [{ value: '', label: t('header.allClients') }, ...(tenants?.map((tenant) => ({ value: tenant.id, label: tenant.name })) || [])]
    : (user?.tenants?.map((tenant) => ({ value: tenant.id, label: tenant.name })) || []);
  const canSelectTenant = isAdmin ? Boolean(tenants?.length) : tenantOptions.length > 1;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="flex min-w-0 flex-wrap items-center gap-2 px-3 py-2 sm:px-6 lg:min-h-16">
        <Button variant="ghost" className="h-11 w-11 shrink-0 p-0 lg:hidden" onClick={onOpenMenu} aria-label={t('nav.openMenu')} aria-expanded={mobileOpen} aria-controls="mobile-navigation"><Menu className="h-5 w-5" /></Button>
        <div className="min-w-0 flex-1">
          {canSelectTenant && (
            <Select
              options={tenantOptions}
              value={selectedTenantId || ''}
              onChange={handleTenantChange}
              className="min-w-0 w-full lg:w-64"
              aria-label={t('settings.client')}
            />
          )}
          {!canSelectTenant && <span className="block truncate text-sm font-semibold">{user?.tenantName || t('nav.dashboard')}</span>}
        </div>

        <div className="flex w-full min-w-0 items-center justify-between gap-1 sm:ml-auto sm:w-auto sm:justify-end sm:gap-2">
          <label className="relative flex items-center text-gray-500" title={t('common.language')}>
            <Languages className="pointer-events-none absolute left-2.5 h-4 w-4" />
            <select
              aria-label={t('common.language')}
              value={i18n.resolvedLanguage || i18n.language}
              onChange={(event) => void i18n.changeLanguage(event.target.value)}
              className="h-11 min-w-0 rounded-lg border border-gray-200 bg-white py-1 pl-8 pr-7 text-base sm:text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {supportedLanguages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
            </select>
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-11 w-11 shrink-0 p-0"
            title={t('header.refresh')}
            aria-label={t('header.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          
          <Button variant="ghost" size="sm" className="h-11 w-11 shrink-0 p-0" title={t('header.notifications')} aria-label={t('header.notifications')}>
            <Bell className="w-4 h-4" />
          </Button>

          <div className="h-6 w-px bg-gray-200" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="h-11 shrink-0 px-2 text-gray-500 hover:text-danger-600 sm:px-3"
            aria-label={t('header.logout')}
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('header.logout')}</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
